import Constants from 'js/Constants';
import { CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT } from 'js/constants/connectionCategories';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import { FileUtil } from 'new/utils/FileUtil/index';

const allowedStatuses = [
  Constants.IngestionStatus.PARTIAL,
  Constants.IngestionStatus.COMPLETE,
  Constants.IngestionStatus.RECOVERED,
  Constants.IngestionStatus.ERROR,
];

export const isReingestAllowed = (
  status: string,
  connectionCategory: string,
  mode: string,
  isAdaptiveFlow: boolean = false,
): boolean => {
  return (
    allowedStatuses.includes(status) &&
    connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT &&
    mode === FILE_LIST_MODE_TYPES.MODE_READ &&
    !isAdaptiveFlow
  );
};

/**
 * Filters the list of files to find those eligible for re-ingestion.
 * @param items
 * @param connectionCategory
 * @param mode
 *
 * const eligibleFiles = getFilesAllowedToReingest(files, connectionCategory, mode);
 */
type FileItem = {
  ingestionStatus: string;
  writeStatus: string;
};

type GetFilesAllowedToReingest = (
  items: FileItem[],
  connectionCategory: string,
  mode: string,
) => FileItem[];

export const getFilesAllowedToReingest: GetFilesAllowedToReingest = (
  items: FileItem[],
  connectionCategory: string,
  mode: string,
) => {
  if (Array.isArray(items) === false) return [];
  if (items.length === 0) return [];
  if (mode === FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES) return [];

  return items.filter((item: { ingestionStatus: any; writeStatus: any }) => {
    const status = item.ingestionStatus || item.writeStatus;
    return FileUtil.isReingestAllowed(status, connectionCategory, mode);
  });
};

/**
 * Builds a payload for re-ingestion by mapping the selected files to their respective file paths.
 * @param selectedFilesForReingestion
 * @param resources
 *
 * const payload = mapFilesForReingestion(selectedFiles, resources);
 *
 * Example output:
 * [
 *    { file: "file1.txt", path: "/root/file1.txt" },
 *    { file: "file2.csv", path: "/root/file2.csv" }
 *  ]
 */
export const mapFilesForReingestion = (
  selectedFilesForReingestion: any[],
  resources: { source_config: any; source_type: any },
) => {
  if (Array.isArray(selectedFilesForReingestion) === false) return [];
  if (selectedFilesForReingestion.length === 0) return [];

  const { source_config, source_type } = resources;

  return selectedFilesForReingestion.reduce((arr, file) => {
    if (file.name) {
      arr.push({
        file: file.name,
        path: FileUtil.getFilePath({
          file,
          sourceConfig: source_config,
          sourceType: source_type,
        }),
      });
    }
    return arr;
  }, []);
};
